import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { COLLECTION_IDENTIFIER_LABELS, type CollectionIdentifier } from '@his/shared';
import { Dialog } from '@/components/ui/layout';
import { Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';

export interface IdentityPatient {
  id: string;
  fullName: string;
  mrn: string;
}

export interface CollectionIdentity {
  patientId: string;
  method: 'wristband_scan' | 'two_identifiers' | 'override';
  scanned?: string;
  value?: string;
  reason?: string;
}

function identifierName(identifier: string): string {
  return identifier in COLLECTION_IDENTIFIER_LABELS ? tr('lab', `identifier_${identifier as CollectionIdentifier}`) : identifier;
}

function identifierHint(identifiers: string[]): string {
  const names = identifiers.map(identifierName);
  if (names.length > 1) return tr('lab', 'identifier_x_or_x', { list: names.slice(0, -1).join(', '), last: names[names.length - 1] });
  return names[0] ?? tr('lab', 'identifier_second');
}

export function identifierLabel(identifiers: string[]): string {
  return tr('lab', 'the_patients_x', { identifier: identifierHint(identifiers) });
}

export function LabIdentityDialog({
  patients,
  identifiers,
  mayOverride,
  onDone,
  onClose,
}: {
  patients: IdentityPatient[];
  identifiers: string[];
  mayOverride: boolean;
  onDone: (identities: CollectionIdentity[]) => void;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [method, setMethod] = useState<CollectionIdentity['method']>('wristband_scan');
  const [text, setText] = useState('');
  const [done, setDone] = useState<CollectionIdentity[]>([]);
  const patient = patients[index];
  const ready = method === 'override' ? text.trim().length >= 5 : text.trim().length >= 2;

  const next = () => {
    const identity: CollectionIdentity =
      method === 'wristband_scan'
        ? { patientId: patient.id, method, scanned: text.trim() }
        : method === 'two_identifiers'
          ? { patientId: patient.id, method, value: text.trim() }
          : { patientId: patient.id, method, reason: text.trim() };
    const all = [...done, identity];
    if (index + 1 >= patients.length) {
      onDone(all);
      return;
    }
    setDone(all);
    setIndex(index + 1);
    setMethod('wristband_scan');
    setText('');
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Confirm the patient${patients.length > 1 ? ` (${index + 1} of ${patients.length})` : ''}`}
      description={tr('lab', 'ask_the_patient_their_full_name_then_check_it', { fullName: patient.fullName, mrn: patient.mrn })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('lab', 'cancel')}
          </Button>
          <Button onClick={next} disabled={!ready}>
            {index + 1 >= patients.length ? tr('lab', 'collect') : tr('lab', 'next_patient')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={tr('lab', 'how_was_the_patient_identified')}>
          <ChoiceButtons<CollectionIdentity['method']>
            layout="cards"
            value={method}
            onChange={(value) => {
              setMethod(value);
              setText('');
            }}
            options={[
              { value: 'wristband_scan', label: tr('lab', 'scanned_the_wristband') },
              { value: 'two_identifiers', label: tr('lab', 'asked_a_second_identifier') },
              ...(mayOverride ? [{ value: 'override' as const, label: tr('lab', 'could_not_identify_supervisor') }] : []),
            ]}
          />
        </Field>
        {method === 'wristband_scan' && (
          <Field label={tr('lab', 'wristband')}>
            <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={tr('lab', 'scan_the_wristband_barcode')} />
          </Field>
        )}
        {method === 'two_identifiers' && (
          <Field label={identifierLabel(identifiers)}>
            <Input autoFocus value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
        )}
        {method === 'override' && (
          <Field label={tr('lab', 'why_the_patient_could_not_be_identified')} hint={tr('lab', 'at_least_5_characters_kept_on_the_specimen')}>
            <Textarea rows={2} value={text} maxLength={300} onChange={(e) => setText(e.target.value)} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
